import { useEffect, useState } from 'react';

import { progress } from '@/api/endpoints';
import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/store/session';
import {
  Loading, Meter, Panel, Row, ScreenBody, ScreenHeader, Stat, StatGrid,
} from '@/components/ui/primitives';

/** Player stats, straight from /progress/statistics — no client-side tallies. */
export function Profile({ onBack }: { onBack: () => void }) {
  const [stats, setStats] = useState<Record<string, unknown> | null>(null);
  const user = useSession((s) => s.user);
  const wallet = useSession((s) => s.wallet);
  const equipment = useSession((s) => s.equipment);
  const characters = useSession((s) => s.characters);

  useEffect(() => { void progress.statistics().then(setStats); }, []);

  const num = (k: string): string => {
    const v = stats?.[k];
    return typeof v === 'number' ? v.toLocaleString('en-US') : String(v ?? '—');
  };
  const xpPct = user && user.xp_to_next > 0 ? (user.xp / user.xp_to_next) * 100 : 0;
  const lampDepth = equipment.lamp?.stats.max_depth_m;
  const depthLabel = lampDepth == null || lampDepth < 0 ? 'endless' : `${lampDepth} m`;

  const bonusLine = (c: typeof characters.explorer): string =>
    c ? `Level ${c.level} · +${c.bonus.value}% ${c.bonus.type.replace(/_/g, ' ')}` : '';

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="hero" size={24} />} title={user?.display_name ?? 'Explorer'} onBack={onBack} />

      <Panel title="LEVEL">
        <p className="font-pixel text-[9px] text-ink-dim">LEVEL {user?.level ?? 1}</p>
        <div className="mt-2"><Meter pct={xpPct} tone="xp" label="Experience toward next level" /></div>
        <p className="mt-1 text-[11px] text-ink-dim tabular">
          {user?.xp ?? 0} / {user?.xp_to_next ?? 0} XP
        </p>
      </Panel>

      <Panel title="WEALTH" className="mt-3">
        <StatGrid>
          <Stat k="Shwe Coins" v={wallet.coins.toLocaleString('en-US')} tone="coin" />
          <Stat k="Shwe Kyat" v={wallet.kyat} tone="kyat" />
          <Stat k="Kyat earned" v={String(stats?.total_kyat_earned ?? '0.00')} tone="kyat" />
          <Stat k="Ancient Keys" v={<span className="flex items-center gap-1"><Icon name="key" size={14} /> {user?.ancient_keys ?? 0}</span>} />
        </StatGrid>
      </Panel>

      {stats ? (
        <Panel title="MINING LOG" className="mt-3">
          <StatGrid>
            <Stat k="Deepest" v={`${num('best_depth_m')} m`} />
            <Stat k="Rocks broken" v={num('total_rocks_mined')} />
            <Stat k="Treasures" v={num('total_treasures_found')} />
            <Stat k="Swings" v={num('total_taps')} />
            <Stat k="Critical hits" v={num('total_crits')} />
            <Stat k="Steps walked" v={num('total_steps')} />
            <Stat k="Ads watched" v={num('total_ads_watched')} />
            <Stat k="Login streak" v={`${num('streak_days')} d`} />
          </StatGrid>
        </Panel>
      ) : <Loading />}

      <Panel title="LOADOUT" className="mt-3">
        <Row
          icon={<Icon name="axe" size={20} />}
          title={equipment.axe?.name ?? '—'}
          subtitle={`Axe level ${equipment.axe?.level ?? 1} · damage ${equipment.axe?.stats.damage ?? 0}`}
        />
        <Row
          icon={<Icon name="lamp" size={20} />}
          title={equipment.lamp?.name ?? '—'}
          subtitle={`Lamp level ${equipment.lamp?.level ?? 1} · reaches ${depthLabel}`}
        />
        {equipment.backpack && (
          <Row
            icon={<Icon name="bag" size={20} />}
            title={equipment.backpack.name}
            subtitle={`${equipment.backpack.stats.slots ?? 0} slots`}
          />
        )}
        <Row
          icon={<Icon name="hero" size={20} />}
          title={characters.explorer?.name ?? 'No explorer'}
          subtitle={bonusLine(characters.explorer) || 'Pick one from the Hero screen'}
        />
        <Row
          icon={<Icon name="companion" size={20} />}
          title={characters.companion?.name ?? 'No companion'}
          subtitle={bonusLine(characters.companion) || 'Unlock Rocky to fill this slot'}
        />
      </Panel>
    </ScreenBody>
  );
}
